import {escapeEntryCard} from '../escape-field.js';
/**
 * **역참 — 먼 길을 돈으로 줄이는 자리.**
 *
 * 이 화면이 답할 것은 둘뿐이다 — *어디로 갈 수 있나 · 얼마인가.*
 * 그래서 **버튼 하나에 이름과 값을 같이 적는다.** 목록을 보고 나서 값을
 * 다시 찾게 하지 않는다.
 *
 * ⚠ **좌표를 안 보낸다.** `{ t:'teleport', to: <정류장 id> }` 만 간다 —
 * 클라가 좌표를 보내게 하면 아무 데나 갈 수 있다. 요금·목적지·내려서는 자리는
 * 전부 서버가 정한다(`town.ts` `teleport`).
 *
 * ⚠ **거절 문장을 여기서 다시 쓰지 않는다.** 없는 곳·제자리·은화 부족은
 * 서버가 이미 말한다. 화면이 같은 말을 따로 들고 있으면 **한쪽이 낡는다** —
 * 이 레포가 그 부류로 여러 번 데였다. 여기서 미리 막는 것은 `buyBtn` 이
 * 모든 화면에서 똑같이 하는 **은화 부족 표시**까지다.
 *
 * 정류장은 `world/teleport.js` 가 **지도에서 도출한다.** 마을이나 갱도 입구가
 * 늘면 이 화면도 저절로 는다 — 여기에 손으로 적는 자리는 없다.
 */
import { STOPS, TELEPORT_MIN, teleportFee } from '../../world/teleport.js';
import { nativeFieldGeometry } from '../../world/native-field.js';
import { buyBtn, fold, muted, num, sec, split, npcHead } from './common.js';

/**
 * 이름 옆에 종류를 안 붙인다. 붙여봤더니 **`마을 마을` · `갱도 입구 갱도 입구`** 가 됐다 —
 * 정류장 이름이 이미 종류를 말하고 있어서, 종류를 덧붙이면 같은 말을 두 번 한다.
 */

/** 서버가 "제자리" 로 치는 거리와 같다(`town.ts`: `< 3`) — 여기서는 회색으로 보인다 */
const HERE = 3;

export function render(ctx) {
  const { y, at } = ctx;
  /**
   * 좌표 판정표 D14 — 2.0 사냥터 소켓 위에서는 좌표가 사냥터 좌표다(1.0 지도보다 넓힌 땅은 같은 숫자의 다른 곳). 더보기 → 마을 사람들로
   * 여기서도 이 창이 열리는데, 요금·「가장 가까운 갱도」·「현재 위치」는 전부 1.0 거리로 재는 문구라 사냥터에서는 적지 않는다(버튼은
   * `where()` 가 'out' 이라 어차피 「마을 밖」으로 꺼진다). 목적지는 권장 레벨 순으로 세운다.
   */
  const field = !!nativeFieldGeometry();

  // 가까운 곳부터. 고르는 기준이 값이라 값 순으로 세운다
  const rows = STOPS
    .map((s) => ({ s, fee: field ? null : teleportFee(y.x, y.y, s), here: !field && Math.hypot(y.x - s.x, y.y - s.y) < HERE }))
    .sort((a, b) => field ? a.s.lvMin - b.s.lvMin : a.fee - b.fee);

  /**
   * `EM` — 목록에 목적지가 **있다는 것**과 사람이 **찾는다는 것**은 다르다.
   *
   * 본토 마을 실좌표에서 `본토 갱도 입구`는 이미 두 번째 줄이었지만 사용자는
   * 없다고 읽었다. 목적지를 한 줄 더 손으로 넣으면 중복만 생긴다. 대신 현재
   * 자리에서 가장 가까운 갱도를 같은 도출 목록에서 골라 설명칸에 먼저 말한다.
   * 갱도나 입구가 늘어도 이 문장은 저절로 새 가장 가까운 곳을 가리킨다.
   */
  const nearestCave = field ? null : rows.find(({ s, here }) => s.kind === 'cave' && !here)
    ?? rows.find(({ s }) => s.kind === 'cave');

  const fairy = rows.find(({ s }) => s.id === 'f:fairy');
  const nativeVillage = rows.find(({ s }) => s.id === 's:native_village');
  // 2026-10-08 — 폰 가로 화면에서 이 설명이 위 칸을 다 채워 목적지 목록이 접힌 아래로 밀렸다(이 화면이 답할 것은 「어디로·얼마」).
  //   위 칸은 머리글만, 설명은 목록 **아래**에 둔다.
  // 2026-10-21 「논문 쓰니?」 — 한 줄(즉시 이동·요금·창고 은화) + 가장 가까운 갱도 한 줄만 보이고, 곳별 안내는 [자세히]
  const notes = [
    field ? '사냥터 안에서는 요금을 적지 않는다. 역참에 닿으면 그 자리에서 잰 요금이 보인다.' : '',
    fairy ? `<b>${fairy.s.ko}</b>는 Lv${fairy.s.lvMin}~${fairy.s.lvMax} 사냥터다. 왕도 남쪽 성벽 밖의 공개 산림길로도 걸어갈 수 있다.` : '',
    nativeVillage ? `<b>원주민 왕도</b>를 선택하면 <b>산림 진입로</b>로 이동한다. ${ctx.y?.admin ? '운영자는 성 안까지 걸어 들어갈 수 있다.' : '플레이어는 성 안에 들어갈 수 없다.'}` : '',
  ].filter(Boolean);
  const summary = `누르면 즉시 이동 · 요금은 거리 비례(최소 ${TELEPORT_MIN} 은화) · 모자라면 창고 은화`;
  const about = sec('역참', (notes.length ? fold(summary, notes.join('<br>')) : muted(summary))
    + (nearestCave
      ? muted(`가장 가까운 갱도는 <b>${nearestCave.s.ko}</b>`
        + `${nearestCave.here ? ' — 지금 여기다.' : ` — ${num(nearestCave.fee)} 은화.`}`)
      : ''));

  const list = sec('목적지', muted('목적지를 누르면 즉시 이동한다. 요금은 가방·창고 은화에서 낸다.') + rows.map(({ s, fee, here }) =>
    '<div class="row">'
    + buyBtn(`Lv${s.lvMin}~${s.lvMax} · ${s.ko}`,
      `act.teleport('${s.id}')`,
      { y, at, need: 'teleport', price: fee, more: here ? ['현재 위치'] : [] })
    + `<span class="qt">${here ? '현재 위치' : fee == null ? '' : `${num(fee)} 은화`}</span>`
    + (s.id === 's:native_village'
      ? '<span class="muted">산림 진입로 도착</span>'
      : '')
    + '</div>').join(''));

  return split(npcHead(), list + escapeEntryCard() + about);
}
